import { useId } from "react";
import { TextInput } from "@/components/ui/Field";
import {
  ekFromListenpreis,
  rabattStaffelLabel,
  type ArtikelRabattState,
  type RabattModus,
  type RabattStaffelRow,
} from "@/lib/artikelRabatt";
import { formatMoneyDe } from "@/lib/artikelZuschlag";
import {
  ArtikelPriceAktivBadge,
  ArtikelPricePassendBadge,
} from "@/pages/artikel/artikelPriceBadges";

export type RabattSelection =
  | { modus: "inaktiv" }
  | { modus: "regel"; regel_id: number }
  | { modus: "manuell_prozent"; manuell_prozent: number | null; manuell_prozent_raw: string };

function parseNum(raw: string): number | null {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function initialSelection(state: ArtikelRabattState | null | undefined): RabattSelection {
  const staffeln = state?.staffeln ?? [];
  if (state?.modus === "manuell_prozent") {
    const pct = state.manuell_prozent ?? state.rabatt_prozent ?? null;
    return {
      modus: "manuell_prozent",
      manuell_prozent: pct,
      manuell_prozent_raw: pct != null ? String(pct) : "",
    };
  }
  const selectedId = state?.modus === "regel" ? state.regel_id : null;
  if (selectedId != null && staffeln.some((s) => s.id === selectedId)) {
    return { modus: "regel", regel_id: selectedId };
  }
  return { modus: "inaktiv" };
}

export function selectionFromRabattState(
  state: ArtikelRabattState | null | undefined,
): RabattSelection {
  return initialSelection(state);
}

export function previewEk(
  selection: RabattSelection,
  listenpreis: number | null | undefined,
  staffeln: RabattStaffelRow[],
): number | null {
  if (selection.modus === "inaktiv") return null;
  if (selection.modus === "regel") {
    const row = staffeln.find((s) => s.id === selection.regel_id);
    if (!row) return null;
    if (row.ek != null) return row.ek;
    if (listenpreis != null && listenpreis > 0) {
      return ekFromListenpreis(listenpreis, row.rabatt_prozent);
    }
    return null;
  }
  if (listenpreis == null || listenpreis <= 0 || selection.manuell_prozent == null) return null;
  return ekFromListenpreis(listenpreis, selection.manuell_prozent);
}

export function previewRabattPct(
  selection: RabattSelection,
  staffeln: RabattStaffelRow[],
): number | null {
  if (selection.modus === "inaktiv") return null;
  if (selection.modus === "regel") {
    return staffeln.find((s) => s.id === selection.regel_id)?.rabatt_prozent ?? null;
  }
  return selection.manuell_prozent;
}

export function ArtikelRabattSection({
  state,
  selection,
  onChange,
  disabled,
  listenpreisOverride,
}: {
  state: ArtikelRabattState | null | undefined;
  selection: RabattSelection;
  onChange: (next: RabattSelection) => void;
  disabled?: boolean;
  listenpreisOverride?: number | null;
}) {
  const radioName = useId();
  const staffeln = state?.staffeln ?? [];
  const listenpreis =
    listenpreisOverride !== undefined ? listenpreisOverride : (state?.listenpreis_netto ?? null);
  const listenpreisOk = listenpreis != null && Number.isFinite(listenpreis) && listenpreis > 0;
  const matchedId = (() => {
    if (!listenpreisOk) return null;
    let best: RabattStaffelRow | null = null;
    for (const row of staffeln) {
      if (listenpreis! + 1e-9 >= (Number(row.ab_listenpreis_netto) || 0)) {
        if (
          !best ||
          (Number(row.ab_listenpreis_netto) || 0) >= (Number(best.ab_listenpreis_netto) || 0)
        ) {
          best = row;
        }
      }
    }
    return best?.id ?? null;
  })();
  const displayStaffeln = staffeln.map((row) => ({
    ...row,
    ek: listenpreisOk ? ekFromListenpreis(listenpreis!, row.rabatt_prozent) : null,
    is_match: matchedId != null && row.id === matchedId,
  }));
  const preview =
    selection.modus === "inaktiv"
      ? (state?.ek_netto ?? null)
      : previewEk(selection, listenpreis, displayStaffeln);
  const previewPct =
    selection.modus === "inaktiv"
      ? (state?.rabatt_prozent ?? null)
      : previewRabattPct(selection, displayStaffeln);

  function selectRegel(id: number) {
    onChange({ modus: "regel", regel_id: id });
  }

  function selectManuellProzent() {
    const raw =
      selection.modus === "manuell_prozent"
        ? selection.manuell_prozent_raw
        : state?.manuell_prozent != null
          ? String(state.manuell_prozent)
          : state?.rabatt_prozent != null
            ? String(state.rabatt_prozent)
            : "";
    onChange({
      modus: "manuell_prozent",
      manuell_prozent: parseNum(raw),
      manuell_prozent_raw: raw,
    });
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-label">Einkaufspreis / Rabatte</h3>
        <p className="mt-1 text-xs text-label-secondary">
          Listenpreis: {listenpreisOk ? `${formatMoneyDe(listenpreis)} €` : "—"}. Entweder ein
          Rabatt oder ein Zuschlag ist aktiv, nicht beides. EK = Listenpreis × (1 − Rabatt/100).
        </p>
      </div>

      {displayStaffeln.length > 0 ? (
        <ul className="space-y-1 rounded-ios border border-separator divide-y divide-separator">
          {displayStaffeln.map((row) => {
            const checked = selection.modus === "regel" && selection.regel_id === row.id;
            const ekLabel = row.ek != null ? `${formatMoneyDe(row.ek)} €` : "—";
            return (
              <li key={row.id}>
                <label
                  className={
                    checked
                      ? "flex cursor-pointer items-center gap-3 bg-accent/5 px-3 py-2.5"
                      : "flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-card-elevated/60"
                  }
                >
                  <input
                    type="radio"
                    name={radioName}
                    className="shrink-0"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => selectRegel(row.id)}
                  />
                  <span className="min-w-0 flex-1 text-sm text-label">
                    {rabattStaffelLabel(row)}
                    {checked ? <ArtikelPriceAktivBadge /> : null}
                    {!checked && row.is_match ? <ArtikelPricePassendBadge /> : null}
                  </span>
                  <span className="shrink-0 font-mono text-sm tabular-nums text-label">
                    EK {ekLabel}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-label-tertiary">
          Keine Rabattstaffeln konfiguriert. Rabatt manuell setzen.
        </p>
      )}

      <label
        className={
          selection.modus === "manuell_prozent"
            ? "flex cursor-pointer items-start gap-3 rounded-ios border border-accent bg-accent/5 px-3 py-2.5"
            : "flex cursor-pointer items-start gap-3 rounded-ios border border-separator px-3 py-2.5"
        }
      >
        <input
          type="radio"
          name={radioName}
          className="mt-2 shrink-0"
          checked={selection.modus === "manuell_prozent"}
          disabled={disabled}
          onChange={selectManuellProzent}
        />
        <div className="min-w-0 flex-1">
          <TextInput
            label={
              <>
                Eigener Rabatt %
                {selection.modus === "manuell_prozent" ? <ArtikelPriceAktivBadge /> : null}
              </>
            }
            value={selection.modus === "manuell_prozent" ? selection.manuell_prozent_raw : ""}
            disabled={disabled || selection.modus !== "manuell_prozent"}
            inputMode="decimal"
            onFocus={() => {
              if (selection.modus !== "manuell_prozent") selectManuellProzent();
            }}
            onChange={(e) => {
              const raw = e.target.value;
              onChange({
                modus: "manuell_prozent",
                manuell_prozent: parseNum(raw),
                manuell_prozent_raw: raw,
              });
            }}
          />
        </div>
      </label>

      <p className="text-sm text-label-secondary">
        Wirksamer EK:{" "}
        <span className="font-mono tabular-nums text-label">
          {preview != null ? `${formatMoneyDe(preview)} €` : "—"}
        </span>
        {previewPct != null ? (
          <>
            {" · "}
            Rabatt:{" "}
            <span className="font-mono tabular-nums text-label">{formatMoneyDe(previewPct)} %</span>
          </>
        ) : null}
      </p>
    </section>
  );
}

export function rabattSelectionToSaveBody(selection: RabattSelection):
  | { modus: RabattModus; regel_id?: number | null; manuell_prozent?: number | null }
  | { error: string } {
  if (selection.modus === "inaktiv") {
    return { error: "Kein Rabatt ausgewählt." };
  }
  if (selection.modus === "regel") {
    return { modus: "regel", regel_id: selection.regel_id };
  }
  if (selection.manuell_prozent == null || selection.manuell_prozent < 0 || selection.manuell_prozent > 100) {
    return { error: "Bitte einen gültigen eigenen Rabatt % eingeben (0–100)." };
  }
  return { modus: "manuell_prozent", manuell_prozent: selection.manuell_prozent };
}
